import { Box, Button, Flex, Heading, Input, InputGroup, SimpleGrid, Text } from '@chakra-ui/react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDebouncedFieldRegistry } from '@/utils/filterUtils/useDebouncedFieldRegistry';
import { ChakraFeatherIcon } from 'src/components/ChakraFeatherIcon';
import { NotificationsTabFilters } from '../../types';
import { InputField } from '@/components/InputField';
import { SelectField } from '@/components/SelectField';
import { formatDateToDateInputValue } from '@/utils/formatDateToDateInputValue';
import { NotificationDispatchStatus } from '@/model/notification-push';

export interface NotificationsTabFilterBoxProps {
  filtersForm: UseFormReturn<NotificationsTabFilters>;
  forceRefetch: () => void;
}

export const NotificationsTabFilterBox = ({ filtersForm }: NotificationsTabFilterBoxProps) => {
  const { t } = useTranslation();
  const { register, reset, watch } = filtersForm;

  const initialDate = watch('startDate');

  return (
    <Box>
      <Flex align='center' gap='pico' mb='deka'>
        <ChakraFeatherIcon color='text.main' name='filter' />
        <Heading variant='sectionTitle'>Filtros</Heading>
      </Flex>

      <Flex direction={{ base: 'row' }} gap='small' rowGap='micro'>
        <SimpleGrid
          gap='deka'
          alignItems='flex-end'
          columns={{ base: 1, sm: 2, md: 4, lg: 5, xl: 6 }}
          rowGap='xxsmall'
          flexGrow={1}
        >
          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>Notificação:</Text>
            <Input variant='flushed' {...useDebouncedFieldRegistry(() => register('search'))} />
          </InputGroup>

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>Data inicial:</Text>
            <InputField type='date' fullDatePickerArea variant='flushed' {...register('startDate')} />
          </InputGroup>

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>Data final:</Text>
            <InputField
              type='date'
              fullDatePickerArea
              variant='flushed'
              min={formatDateToDateInputValue(initialDate ?? '')}
              {...register('endDate')}
            />
          </InputGroup>

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>
              {t('pages.communication_central.components.notifications_tab_filter_box.status_label')}
            </Text>
            {/*
              ATENÇÃO (Fase 4 do plano de agendamento): o GET de notificações hoje
              (`notifications-repository.ts`) aceita só `status=SEND|SCHEDULED`,
              calculado dinamicamente a partir de `scheduled_at`. A Fase 2 (rodando
              em paralelo em `admin-config-service-node`) está migrando para um
              `status` persistido de 4 valores (PENDING/PROCESSING/SENT/FAILED).
              Este select já expõe os 4 valores reais para o usuário — o mapeamento
              final do `value` de cada `option` para o parâmetro de query aceito
              pelo endpoint precisa ser confirmado/ajustado depois que a Fase 2/3
              do backend terminar (pode ser necessário mapear PENDING/PROCESSING
              para o `SCHEDULED` legado, ou o backend pode já aceitar os 4 valores
              diretamente).
            */}
            <SelectField variant='flushed' {...register('status')}>
              <option value=''>{t('pages.communication_central.components.notifications_tab_filter_box.status_all')}</option>
              {/*
                PENDING e PROCESSING aparecem como uma única opção "Agendado" —
                PROCESSING é um estado transitório (só existe entre o claim
                atômico do scheduler e o disparo, tipicamente < 1s) e não vale
                expor como filtro separado; o badge da tabela já trata os dois
                como o mesmo conceito pro usuário final.
              */}
              <option value={NotificationDispatchStatus.PENDING}>
                {t('pages.communication_central.components.notifications_tab_table.status_pending')}
              </option>
              <option value={NotificationDispatchStatus.SENT}>
                {t('pages.communication_central.components.notifications_tab_table.status_sent')}
              </option>
              <option value={NotificationDispatchStatus.FAILED}>
                {t('pages.communication_central.components.notifications_tab_table.status_failed')}
              </option>
            </SelectField>
          </InputGroup>
        </SimpleGrid>

        <Button alignSelf={{ lg: 'end' }} colorScheme='text' variant='outline' mt='10px' onClick={() => reset()}>
          Limpar filtros
        </Button>
      </Flex>
    </Box>
  );
};
